/* NumberType.js */

"use strict";

/**
 * @type {{Control:{Textfield:{InputNumber:InputNumberControlComponent}, Boolean:{InputCheckbox:InputCheckboxControlComponent}}}}
 * @const
 */
const uiCore = require("@gluenetworks/steve_ui_core");
const InputNumberControl = uiCore.Control.Textfield.InputNumber;
const InputCheckboxControl = uiCore.Control.Boolean.InputCheckbox;

class NumberType {
    /**
     * @typedef {{[min]: number, [max]: number, [onlyInteger]: boolean}} NumberInfo
     * @typedef {{Number: NumberInfo}} NumberTypeInfo - Number type info object
     */

    constructor() {
        /**
         * @type {?NumberInfo}
         * @private
         */
        this._initialData = {};

        /**
         * Root html Element
         * @type {?HTMLDivElement}
         */
        this.rootElem = this._createRootElement();

        /**
         * @type {?InputNumberControlComponent}
         */
        this.inputMax = this._createInput({
            placeholder: "Max value"
        });

        /**
         * @type {?InputNumberControlComponent}
         */
        this.inputMin = this._createInput({
            placeholder: "Min value"
        });

        /**
         * @type {?InputCheckboxControlComponent}
         */
        this.inputInteger = this._createCheckbox({ label: "Integer Only" });

        this._registerListeners();
    }

    get rootElement() {
        return this.getRoot();
    }

    /********************************************/
    /* START PRIVATE COMPONENT MODEL METHODS    */

    /********************************************/

    _registerListeners() {
        this.rootElem.addEventListener("click", (this._onRootClick = this._onRootClick.bind(this)));
        this.rootElem.addEventListener(
            "dblclick",
            (this._onRootClick = this._onRootClick.bind(this))
        );
        this.inputInteger.addListener(
            "UserValue",
            (this._onChangeInteger = this._onChangeInteger.bind(this))
        );
    }

    _unregisterListeners() {
        this.rootElem.removeEventListener("click", this._onRootClick);
        this.rootElem.removeEventListener("dblclick", this._onRootClick);
        this.inputInteger.removeListener("click", this._onChangeInteger);
    }

    _shutdown() {
        this._unregisterListeners();

        return Promise.all([
            this.inputMax.shutdown(),
            this.inputMin.shutdown(),
            this.inputInteger.shutdown()
        ])
            .then(() => {
                this.rootElem.remove();
            })
            .then(() => {
                this._initialData = null;
                this.inputMin = null;
                this.inputMax = null;
                this.inputInteger = null;
                this.rootElem = null;
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT MODEL METHODS      */
    /* START PUBLIC COMPONENT MODEL METHODS     */

    /********************************************/

    getRoot() {
        return this.rootElem;
    }

    /********************************************/
    /* END COMPONENT MODEL METHODS              */
    /* START HANDLER METHODS                    */
    /********************************************/
    _onRootClick(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    _onChangeInteger(state) {
        const stepAttribute = state ? "1" : "any";
        this.inputMax.setAttribute("step", stepAttribute);
        this.inputMin.setAttribute("step", stepAttribute);
    }
    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE METHODS                    */
    /********************************************/

    /********************************************/
    /* END PRIVATE METHODS                      */
    /* START PRIVATE RENDER METHODS             */

    /********************************************/

    _render() {
        this.rootElem.innerHTML = "";

        this.rootElem.appendChild(this._createInputGroup("", this.inputInteger));
        this.rootElem.appendChild(this._createInputGroup("Min", this.inputMin));
        this.rootElem.appendChild(this._createInputGroup("Max", this.inputMax));
    }

    /**
     *
     * @returns {HTMLDivElement}
     * @private
     */
    _createRootElement() {
        const rootElem = document.createElement("div");
        rootElem.classList.add("number-editor", "inner-editor");
        rootElem.component = this;
        return rootElem;
    }

    _createInput(options) {
        return new InputNumberControl(Object.assign({ darkTheme: true }, options));
    }

    _createCheckbox(options) {
        return new InputCheckboxControl(
            Object.assign(
                {
                    labelShown: true,
                    value: false,
                    darkTheme: true
                },
                options
            )
        );
    }

    _createInputGroup(labelString, inputComponent) {
        const wrapper = document.createElement("div");
        const labelEl = document.createElement("label");
        const inputId = `input_${labelString.replace(/[^\w]/g, "-")}`;

        wrapper.classList.add("input-wrapper");

        labelEl.setAttribute("for", inputId);
        labelEl.innerText = labelString;

        inputComponent.setAttribute("id", inputId);

        wrapper.appendChild(labelEl);
        wrapper.appendChild(inputComponent.renderer.rootElement);
        return wrapper;
    }
    /********************************************/
    /* END PRIVATE RENDER METHODS               */
    /* START PRIVATE API METHODS                */

    /********************************************/

    /**
     *
     * @param {NumberInfo} [data = {}]
     * @private
     */
    _resetData(data = {}) {
        this._initialData = data;
        this.inputMax.setValue(data.hasOwnProperty("max") ? data.max : "");
        this.inputMin.setValue(data.hasOwnProperty("min") ? data.min : "");
        this.inputInteger.setValue(data.onlyInteger);

        const stepAttribute = data.onlyInteger ? "1" : "any";
        this.inputMax.setAttribute("step", stepAttribute);
        this.inputMin.setAttribute("step", stepAttribute);
    }

    /**
     * @returns {NumberInfo}
     * @private
     */
    _getData() {
        const data = {};
        const min = this.inputMin.getValue();
        const max = this.inputMax.getValue();
        const onlyInteger = this.inputInteger.getValue();

        Object.assign(
            data,
            Number.isFinite(max) && { max },
            Number.isFinite(min) && { min },
            Boolean(onlyInteger) && { onlyInteger }
        );

        return data;
    }

    _focus() {
        this.inputMin.focus();
    }

    /********************************************/
    /*  END PRIVATE API METHODS                 */
    /* START PUBLIC API METHODS                 */

    /********************************************/

    destroy() {
        this._shutdown().catch(console.error);
    }

    /**
     *
     * @param {NumberTypeInfo} numberTypeInfo - enum type info object
     */
    setData(numberTypeInfo) {
        const data = (numberTypeInfo && numberTypeInfo.Number) || {};
        this._resetData(data);
        this._render();
        this._focus();
    }

    /**
     *
     * @returns {NumberTypeInfo}
     */
    getData() {
        return {
            Number: this._getData()
        };
    }

    /**
     *
     * @returns {boolean}
     */
    isValueChanged() {
        return JSON.stringify(this._initialData) !== JSON.stringify(this._getData());
    }

    /**
     *
     * @returns {{valid: boolean, msg: string}}
     */
    validate() {
        const { min, max, onlyInteger } = this._getData();
        const validObj = {
            valid: true,
            msg: ""
        };

        if (min !== null && max !== null && max < min) {
            Object.assign(validObj, {
                valid: false,
                msg: "Max should be greater then Min"
            });
        }

        if (
            onlyInteger &&
            ((min !== null && min !== Math.trunc(min)) || (max !== null && max !== Math.trunc(max)))
        ) {
            Object.assign(validObj, {
                valid: false,
                msg: "Values should be Integer"
            });
        }

        return validObj;
    }

    focus() {
        this._focus();
    }

    /********************************************/
    /* END PUBLIC API METHODS                   */
    /********************************************/
}

module.exports = NumberType;
